import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { loadingManager } from "./loadingManager";
import { MESSAGE_ROTATE_MS } from "./constants";
import { PROGRESS_MODE } from "./progressMode";
import { pickMessage } from "./loadingMessages";
import GlobalLoadingHost from "./components/GlobalLoadingHost";

const LoadingContext = createContext(null);

export function LoadingProvider({ children }) {
  const [state, setState] = useState(() => loadingManager.getState());

  useEffect(() => loadingManager.subscribe(setState), []);

  useEffect(() => {
    if (!state.isLoading) return undefined;
    if (state.progressMode === PROGRESS_MODE.DETERMINATE) return undefined;
    const timer = setInterval(() => loadingManager.rotateMessage(), MESSAGE_ROTATE_MS);
    return () => clearInterval(timer);
  }, [state.isLoading, state.progressMode]);

  const displayMessage = useMemo(() => {
    if (state.message) return state.message;
    return pickMessage(state.taskType, state.messageIndex);
  }, [state.message, state.taskType, state.messageIndex]);

  const startLoading = useCallback((options) => loadingManager.startLoading(options), []);
  const stopLoading = useCallback((id) => loadingManager.stopLoading(id), []);
  const setProgress = useCallback((v) => loadingManager.setProgress(v), []);
  const setTaskProgress = useCallback(
    (id, current, total, message, unit) =>
      loadingManager.setTaskProgress(id, current, total, message, unit),
    []
  );
  const setTaskMessage = useCallback((id, message) => loadingManager.setTaskMessage(id, message), []);
  const setError = useCallback((err) => loadingManager.setError(err), []);
  const clearError = useCallback(() => loadingManager.clearError(), []);

  const withLoading = useCallback(
    (fn, options) => loadingManager.withLoading(fn, options),
    []
  );

  const value = useMemo(
    () => ({
      ...state,
      displayMessage,
      startLoading,
      stopLoading,
      setProgress,
      setTaskProgress,
      setTaskMessage,
      setError,
      clearError,
      withLoading,
    }),
    [
      state,
      displayMessage,
      startLoading,
      stopLoading,
      setProgress,
      setTaskProgress,
      setTaskMessage,
      setError,
      clearError,
      withLoading,
    ]
  );

  return (
    <LoadingContext.Provider value={value}>
      {children}
      <GlobalLoadingHost state={state} displayMessage={displayMessage} onRetry={clearError} />
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const ctx = useContext(LoadingContext);
  if (!ctx) {
    throw new Error("useLoading must be used within LoadingProvider");
  }
  return ctx;
}

export function useLoadingOptional() {
  const ctx = useContext(LoadingContext);
  if (!ctx) {
    return {
      isLoading: false,
      progressMode: PROGRESS_MODE.INDETERMINATE,
      progressPercent: null,
      startLoading: () => "",
      stopLoading: () => {},
      setTaskProgress: () => {},
      setTaskMessage: () => {},
      withLoading: (fn) => fn(),
      displayMessage: "",
    };
  }
  return ctx;
}
